import React, { Fragment, useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableCheckboxCell,
    DataTableColumnHeaderCell,
    DataTableContent,
    DataTableHeaderCheckboxCell,
    DataTableHeaderRow,
    DataTablePagination,
    DataTableRow,
    DataTableToolbar,
    Dialog,
    Search,
    SortDirection,
} from "@episerver/ui-framework";
import {
    addContactsTitleSelector,
    contactListPaginationSelector,
    deselectAllContacts,
    deselectContact,
    isOpenDialogSelector,
    keywordSelector,
    onChangeDialog,
    selectAllContacts,
    selectContact,
    selectedContactsSelector,
    sort,
    sortSelector,
    changeKeyword,
    contactsSelector,
} from "../reducers/addContactsToOrganizationReducer";
import { Contact, ContactsType, ServerSortType } from "../../ContactManager/model/contact";
import { fetchContacts, searchContacts } from "../../ContactManager/reducers/asyncThunks";
import { debounce } from "lodash-es";
import { onAddOrUpdateContact, organizationSelector } from "../reducers/organizationDetailReducer";

interface AddContactsToOrganizationProps {
    organizationId: string;
}

const AddContactsToOrganization = (props: AddContactsToOrganizationProps) => {
    const dispatch = useAppDispatch();
    const contacts = useAppSelector(contactsSelector);
    const organization = useAppSelector(organizationSelector);
    const { sortDirection, sortedColumn } = useAppSelector(sortSelector);
    const sortDirectionRef = useRef<SortDirection>(sortDirection);
    const sortedColumnRef = useRef<string>(sortedColumn);
    const { organizationId } = props;
    const { rowsPerPage, startRow, rowsPerPageOptions, totalRowCount } = useAppSelector(contactListPaginationSelector);
    const keyword = useAppSelector(keywordSelector);
    const selectedContacts = useAppSelector(selectedContactsSelector);
    const open = useAppSelector(isOpenDialogSelector);
    const title = useAppSelector(addContactsTitleSelector);

    useEffect(() => {
        if (keyword) {
            dispatch(
                searchContacts({
                    query: keyword,
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: rowsPerPage,
                    type: ContactsType.Contacts
                })
            );
        } else {
            dispatch(
                fetchContacts({
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: rowsPerPage,
                    type: ContactsType.Contacts
                })
            );
        }
    }, []);

    const columns: Array<{ selector: string; title: string; sortable: boolean }> = [
        { selector: "FullName", title: "Full Name", sortable: true },
        { selector: "Email", title: "Email", sortable: true },
    ];

    const onSelectedRowChanged = (row: Contact, checked: boolean): void => {
        if (checked) dispatch(selectContact(row));
        else dispatch(deselectContact(row));
    };

    const onToggleAllSelected = (checked: boolean): void => {
        if (checked) dispatch(selectAllContacts());
        else dispatch(deselectAllContacts());
    };

    const onSort = (column: string, direction: SortDirection): void => {
        dispatch(sort({ column, direction }));
        if (keyword) {
            dispatch(
                searchContacts({
                    query: keyword,
                    sortField: column,
                    sortType: direction === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: 0,
                    recordsToRetrieve: rowsPerPage,
                    type: ContactsType.Contacts
                })
            );
        } else {
            dispatch(
                fetchContacts({
                    sortField: column,
                    sortType: direction === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: 0,
                    recordsToRetrieve: rowsPerPage,
                    type: ContactsType.Contacts
                })
            );
        }
    };

    useEffect(() => {
        sortDirectionRef.current = sortDirection;
    }, [sortDirection]);

    useEffect(() => {
        sortedColumnRef.current = sortedColumn;
    }, [sortedColumn]);

    const isSelected = (row: Contact): boolean => {
        return organization.contacts.find((x) => x.primaryKeyId == row.primaryKeyId) != null;
    };

    const handleSearch = (value: string, recordsToRetrieve: number) => debouncedSearch(value, recordsToRetrieve);
    const debouncedSearch = useCallback(
        debounce((nextValue: string, recordsToRetrieve: number) => onSearch(nextValue, recordsToRetrieve), 1000),
        []
    );
    const onSearch = (value: string, recordsToRetrieve: number): void => {
        if (value !== "") {
            dispatch(
                searchContacts({
                    query: value,
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: recordsToRetrieve,
                    type: ContactsType.Contacts
                })
            );
        } else {
            dispatch(
                fetchContacts({
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: rowsPerPage,
                    type: ContactsType.Contacts
                })
            );
        }
        dispatch(changeKeyword(value));
    };

    const onChangePage = (startRow: number): void => {
        if (keyword) {
            dispatch(
                searchContacts({
                    query: keyword,
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: rowsPerPage,
                    type: ContactsType.Contacts
                })
            );
        } else {
            dispatch(
                fetchContacts({
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: rowsPerPage,
                    type: ContactsType.Contacts
                })
            );
        }
    };

    const onChangeRowsPerPage = (rowsPerPage: number): void => {
        if (keyword) {
            dispatch(
                searchContacts({
                    query: keyword,
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: rowsPerPage,
                    type: ContactsType.Contacts
                })
            );
        } else {
            dispatch(
                fetchContacts({
                    sortField: sortedColumnRef.current,
                    sortType:
                        sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                    startIndex: startRow,
                    recordsToRetrieve: rowsPerPage,
                    type: ContactsType.Contacts
                })
            );
        }
    };

    const onSubmit = async (): Promise<void> => {
        await dispatch(
            onAddOrUpdateContact({
                organizationId: organizationId,
                contacts: selectedContacts,
            })
        );

        dispatch(deselectAllContacts());
        dispatch(changeKeyword(""));
        dispatch(
            fetchContacts({
                sortField: sortedColumnRef.current,
                sortType:
                    sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: startRow,
                recordsToRetrieve: rowsPerPage,
                type: ContactsType.Contacts
            })
        );
        dispatch(onChangeDialog({ open: false }));
    };

    const renderRow = (rowData: Contact): React.ReactElement => {
        return (
            <DataTableRow rowId={rowData.primaryKeyId!} key={rowData.primaryKeyId} selected={isSelected(rowData)}>
                <DataTableCheckboxCell
                    defaultChecked={isSelected(rowData)}
                    onChange={(e) => onSelectedRowChanged(rowData, e.target.checked)}
                />
                <DataTableCell>{rowData.fullName}</DataTableCell>
                <DataTableCell>{rowData.email ?? ""}</DataTableCell>
            </DataTableRow>
        );
    };

    return (
        <Dialog
            open={open}
            title={title}
            dismissLabel="Cancel"
            confirmLabel="Add"
            enableConfirm={selectedContacts.length > 0}
            onInteraction={(evt) => {
                if (evt.detail.action === "confirm") {
                    onSubmit();
                } else {
                    dispatch(onChangeDialog({ open: false }));

                    if (keyword) {
                        dispatch(
                            searchContacts({
                                query: keyword,
                                sortField: sortedColumnRef.current,
                                sortType:
                                    sortDirectionRef.current === SortDirection.Ascending
                                        ? ServerSortType.Asc
                                        : ServerSortType.Desc,
                                startIndex: startRow,
                                recordsToRetrieve: rowsPerPage,
                                type: ContactsType.Contacts
                            })
                        );
                    } else {
                        dispatch(
                            fetchContacts({
                                sortField: sortedColumnRef.current,
                                sortType:
                                    sortDirectionRef.current === SortDirection.Ascending
                                        ? ServerSortType.Asc
                                        : ServerSortType.Desc,
                                startIndex: startRow,
                                recordsToRetrieve: rowsPerPage,
                                type: ContactsType.Contacts
                            })
                        );
                    }
                }
            }}
        >
            <div className="organization-list">
                <DataTable>
                    <DataTableToolbar className="organization-list__toolbar">
                        <Search
                            onValueChange={(value: string) => {
                                handleSearch(value, rowsPerPage);
                            }}
                            onSearch={(value: string) => {
                                handleSearch(value, rowsPerPage);
                            }}
                            value={keyword}
                        />
                    </DataTableToolbar>
                    <DataTableContent>
                        <DataTableHeaderRow>
                            <Fragment>
                                {contacts.length > 0 && (
                                    <DataTableHeaderCheckboxCell
                                        onChange={(e) => onToggleAllSelected(e.target.checked)}
                                    />
                                )}
                            </Fragment>
                            <React.Fragment>
                                {columns.map((column) => {
                                    if (column.sortable)
                                        return (
                                            <DataTableColumnHeaderCell
                                                key={column.selector}
                                                onSort={(_header: string, direction: SortDirection) =>
                                                    onSort(column.selector, direction)
                                                }
                                                sortDirection={
                                                    sortedColumn === column.selector
                                                        ? sortDirection
                                                        : SortDirection.Unsorted
                                                }
                                            >
                                                {column.title}
                                            </DataTableColumnHeaderCell>
                                        );
                                    return (
                                        <DataTableColumnHeaderCell key={column.selector}>
                                            {column.title}
                                        </DataTableColumnHeaderCell>
                                    );
                                })}
                            </React.Fragment>
                        </DataTableHeaderRow>
                        <DataTableBody>
                            {contacts.map((row: Contact) => {
                                return renderRow(row);
                            })}
                        </DataTableBody>
                    </DataTableContent>
                    <DataTablePagination
                        startRow={startRow}
                        totalRowCount={totalRowCount}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={rowsPerPageOptions}
                        onChangePage={(startRow) => onChangePage(startRow)}
                        onChangeRowsPerPage={(rowsPerPage) => onChangeRowsPerPage(rowsPerPage)}
                    />
                </DataTable>
            </div>
        </Dialog>
    );
};

export default AddContactsToOrganization;
